import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * **Tag chips** from a plain array: `draw()` rebuilds the chip DOM so each remove button closes over the right index.
 * Enter commits trimmed text (deduped); Backspace with empty input pops the last tag — common Gmail-style behavior.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'tag-input',
  category: 'forms-flow',
  title: 'Tag input',
  description: 'Enter adds chips; Backspace removes last; click × removes one.',
  tags: [T.tags, T.input, T.keyboard],
  html: `<h2>Tags</h2>

<!-- Single-line flex row: chips (left) + growing text field; border wraps the whole “fake input” -->
<div class="wrap" id="wrap">
  <div id="tags" class="tags"></div>
  <input id="inp" type="text" placeholder="Type + Enter" aria-label="Add tag" />
</div>
<p class="small">Backspace removes last tag.</p>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 0.75rem;
}

.wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  max-width: 24rem;
  padding: 6px;
  border: 1px solid var(--nogrok-preview-outline);
  border-radius: 8px;
  background: var(--nogrok-preview-surface-container);
  min-height: 44px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* No inner border — looks like one composite control */
input {
  flex: 1;
  min-width: 6rem;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--nogrok-preview-on-surface);
  font: inherit;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--nogrok-preview-surface-container-high);
  border: 1px solid color-mix(in srgb, var(--nogrok-preview-primary) 25%, transparent);
  font-size: 0.8rem;
}

.chip button {
  font: inherit;
  border: 0;
  background: transparent;
  color: var(--nogrok-preview-accent-warm);
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
}

.small {
  font-size: 0.75rem;
  margin-top: 0.5rem;
}`,
  js: `const tags = []
const box = document.getElementById('tags')
const inp = document.getElementById('inp')

function draw() {
  if (!box) return
  box.innerHTML = ''
  tags.forEach((t, i) => {
    const sp = document.createElement('span')
    sp.className = 'chip'
    sp.append(t)
    const x = document.createElement('button')
    x.type = 'button'
    x.textContent = '×'
    x.addEventListener('click', () => {
      tags.splice(i, 1)
      draw()
    })
    sp.appendChild(x)
    box.appendChild(sp)
  })
}

inp?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    const v = inp.value.trim()
    if (v && !tags.includes(v)) {
      tags.push(v)
      inp.value = ''
      draw()
    }
  }
  if (e.key === 'Backspace' && !inp.value && tags.length) {
    tags.pop()
    draw()
  }
})

draw()`,
}

export default preset
